import { useParams, useNavigate, Link } from 'react-router-dom';
import { Alert, Button, Card, Result, Spin, Typography } from 'antd';
import {
  useAcceptInvitation,
  useDeclineInvitation,
  useInvitation,
} from '@presentation/hooks/useInvitations';
import { paths } from '@app/routes/paths';

export const AcceptInvitationPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data: invitation, isLoading, isError } = useInvitation(token);
  const acceptInvitation = useAcceptInvitation();
  const declineInvitation = useDeclineInvitation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Result
          status="error"
          title="Invitation not found"
          subTitle="This invitation link is invalid or has been used already."
          extra={<Link to={paths.dashboard}>Go to dashboard</Link>}
        />
      </div>
    );
  }

  if (invitation.status !== 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Result
          status="info"
          title={`Invitation ${invitation.status}`}
          extra={<Link to={paths.dashboard}>Go to dashboard</Link>}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm text-center">
        <Typography.Title level={4} className="!mb-2">
          You&apos;re invited
        </Typography.Title>
        <Typography.Text type="secondary">
          <Typography.Text strong>{invitation.invitedBy.name}</Typography.Text> invited you to join{' '}
          <Typography.Text strong>{invitation.workspace?.name}</Typography.Text> as{' '}
          {invitation.role.name}.
        </Typography.Text>

        {acceptInvitation.isError && (
          <Alert type="error" showIcon title={acceptInvitation.error.message} className="mt-4" />
        )}

        <div className="mt-6 flex justify-center gap-2">
          <Button
            type="primary"
            loading={acceptInvitation.isPending}
            onClick={() =>
              token &&
              acceptInvitation.mutate(token, {
                onSuccess: () => navigate(paths.workspaces, { replace: true }),
              })
            }
          >
            Accept
          </Button>
          <Button
            loading={declineInvitation.isPending}
            onClick={() =>
              token &&
              declineInvitation.mutate(token, {
                onSuccess: () => navigate(paths.dashboard, { replace: true }),
              })
            }
          >
            Decline
          </Button>
        </div>
      </Card>
    </div>
  );
};
